/**
 * Meter schemas — types for the per-plan usage/quota data served by
 * sk-catalog-api GET /meter/usage (backed by @softknack/meter → sk-meter).
 * Mirrors the backend MeterUsageResult contract.
 */

/** A prepaid add-on block that can raise a metric's cap (recharge unit). */
export interface MeterAddonBlock {
  size: number;
  price_inr: number;
}

/** Usage for one metric (e.g. 'cat.items'), as reported by the meter. */
export interface MeterMetricUsage {
  metric: string;
  used: number;
  /** Effective cap = tier cap + prepaid blocks. null when not enforced. */
  effective_cap: number | null;
  tier_cap?: number | null;
  blocks_purchased_this_period?: number;
  /** true when at/over the cap — new writes are refused until recharge/upgrade. */
  blocked: boolean;
  shape?: 'flow' | 'gauge';
  scope?: 'org' | 'user';
  period?: string;
  enforced?: boolean;
  addon_block?: MeterAddonBlock;
}

export interface MeterUsageResponse {
  /** false when metering is off/unconfigured for this deployment. */
  enabled: boolean;
  /** true when the meter was unreachable — show last-known / "updating…". */
  degraded: boolean;
  metrics: MeterMetricUsage[];
}

/** Machine-readable payload carried by a 402 AT_CAP_BLOCKED error (error.meta). */
export interface QuotaBlockedMeta {
  action: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  scope?: 'org' | 'user';
  recharge_options: { metric: string; block_size: number; block_price_inr: number }[];
  upgrade_url: string | null;
}

/** The catalog's item-count metric key (matches backend METER_METRICS). */
export const METRIC_CATALOG_ITEMS = 'cat.items' as const;
